<template>
    <div    class="px-2 py-1 my-1 w-full">
        <div    v-if="loading">
            <div class="flex flex-row items-center justify-center py-4">
                <span :class="iconLoading"></span>
                <span class="text-grey-500 text-base font-medium">Loading Graph Data, please wait.</span>
            </div>
        </div>

        <div    v-else-if="loadingChartData"
                class="flex flex-row items-center justify-center py-4">
            <span :class="iconLoading"></span>
            <span class="text-grey-500 text-base font-medium">Loading Data on Graph.</span>
        </div>

        <div    v-if="this.numData === 0"
                class="text-md">
            <p>
                No data found in specified range.
            </p>
            <p>
                Last Check-in was {{ prettyLastUpdateTime }}.
            </p>
        </div>

        <div    class="flex justify-end">
            <div    class="w-2/5 flex flex-row">
                <!-- Select Point To Show -->
                <!-- I'm Just disabling the select for the moment.  UI wise it is just gonna be hard to pull off. -->
                <div    v-if="permissions.isSonicu() && Object.keys(pairPointOptions).length > 0 && false"
                        class="w-full mb-1 px-1 w-full flex justify-end">
                    <div    class="py-2 px-4 text-white rounded-md flex items-center"
                    >
                        <select2        :options="pairPointOptions"
                                        v-model:value="pairedPoint"
                                        placeholder="Select A Point To Show"
                                        :allow-clear="true"
                                        :close-on-select="true"
                        ></select2>

                    </div>
                </div>

                <!-- Timezone Selection -->
                <div    v-if="permissions.isSonicu() && this.numData > 0"
                        class="mb-1 px-2 w-full flex justify-end">
                    <div    class="py-2 px-4 border-solid border-gray-900 bg-blue-500 text-white rounded-md flex items-center">
                        <div    class="flex items-center">
                            <small-colored-check
                                v-model="useUTC"
                                :togglable="true"
                            ></small-colored-check>
                            <span   class="lnr icon-earth"></span>
                        </div>
                        <span   class="ml-2">{{ timezone }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="relative border-gray-300 rounded-md"
            :class="[numData > 0 ? 'border border-solid' : '']"
            :style="graphHeight">
            <div    v-show="numData === 0 || this.lastDataIndex === 0"
                    class="z-50 absolute bg-white w-full"
                    :class="[numData === 0 || this.lastDataIndex === 0 ? 'z-50' : '']"
                    :style="graphHeight"></div>
            <div    class="absolute w-full z-10"
                    id="meter-graph"
                    ref="meterGraph"
                    :style="graphHeight"
            ></div>
        </div>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from "@/mixins/IconNamesMixin"

import {am4ChartMixinLegacy} from "@/mixins/am4charts/am4ChartMixinLegacy";
import {am4AxisMixin} from "@/mixins/am4charts/am4AxisMixin";
import {am4SeriesMixin} from "@/mixins/am4charts/am4SeriesMixin";
import {am4ScrollbarXMixinLegacy} from "@/mixins/am4charts/am4ScrollbarXMixinLegacy";
import {am4DateAxisGroupingMixin} from "@/mixins/am4charts/am4DateAxisGroupingMixin";
import {am4LegendMixin} from "@/mixins/am4charts/am4LegendMixin";
import {am4Settings} from "@/mixins/am4charts/am4Settings";

import axios from "axios"

// AM4 Charts
import {amchartsMixin} from "@/mixins/amchartsMixin"
import * as am4maps from "@amcharts/amcharts4/maps";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import SmallColoredCheck from "@/components/elements/SmallColoredCheck";
import {SeriesLegacy} from "@/mixins/am4charts/seriesLegacy"
import Select2 from "@/components/elements/select2";

export default {
    name: "MeterGraphFullLegacy",
    mixins: [SonicuCoreMixin, iconNamesMixin, amchartsMixin,
        am4ChartMixinLegacy, am4AxisMixin, am4SeriesMixin, am4ScrollbarXMixinLegacy,
        am4DateAxisGroupingMixin, am4LegendMixin
    ],
    components: {Select2, SmallColoredCheck},
    props: {
        id: Number,
        name: String,
        useShortGaps: Boolean,
        startDate: {
            type: Date,
            default() {
                return (new Date()).setDate(new Date().getDay() - 1)
            },
        },
        endDate: {
            type: Date,
            default() {
                return new Date()
            }
        },
        fileName: String,
        timezone: String,
        filterDataReady: {
            type: Boolean,
            default: true,
        },
        pairPoints: {},
        pairPointOptions: {},
        pairPointId: null,
        showPairedPoint: Boolean,
    },
    data() {
        return {
            leftMeterIds: [`${this.id}`],
            rightMeterIds: [],

            loadFunctionTmp: null,
            updateGraphDebounce: null,
            loading: true,
            loadingChartData: false,
            chartReady: false,
            leftMin: 10000,
            leftMax: -10000,
            rightMin: 10000,
            rightMax: -10000,
            numData: 0,
            lastUpdateTime: null,
            lastLoadedDate: null,
            loadFunction: null,
            requestNumber: 0,
            lastDataIndex: 0,

            formatter: null,
            companyTimezone: null,
            useUTC: false,
            pairedPoint: null,
            legend: null,
        }
    },
    created() {
        // This is required by the Amcharts/Vuejs integration
        // https://www.amcharts.com/docs/v4/getting-started/integrations/using-vue-js/#selenium-highlight)
        this.chart = null
        this.chartEvents = []
        this.seriesList = {}

        // Now you can utilize the rest of the objects like normal
        this.initializeAmchartsNoAnimation()
        this.useUTC = false
        this.formatter = new am4core.DateFormatter()
        this.formatter.timezone = this.timezone
        this.loadFunctionTmp = _.debounce(() => {
            this.loading = true
            this.requestNumber++
            this.lastDataIndex = 0
            this.lastLoadedDate = null
            this.leftMin = 10000
            this.leftMax = -10000
            this.rightMin = 10000
            this.rightMax = -10000

            if (this.chart) {
                this.chart.data = []
            }

            let allIds = this.leftMeterIds.concat(this.rightMeterIds)
            let numberIdsComplete = allIds.length
            allIds.forEach(item => {
                axios.get(this.company.buildCompanyApiInternalRoute('graph-meter-legacy') + '/' + item, {
                    params: {
                        startDate: this.startDate.toISOString(),
                        endDate: this.endDate.toISOString(),
                        requestNumber: this.requestNumber,
                    }
                }).then(
                    response => {
                        this.loading = false
                        if (parseInt(response.data.requestNumber) !== this.requestNumber) {
                            return
                        }
                        let seriesData = {
                            id: item,
                            requestNumber: response.data.requestNumber,
                            alarmInfo: response.data.alarmInfo,
                            min: response.data.min,
                            max: response.data.max,
                            numData: response.data.numData,
                            lastUpdateTime: response.data.lastUpdateTime
                        }
                        let s = this.seriesList[item]
                        if (!_.has(this.seriesList, item)) {
                            s = new SeriesLegacy(this.chart, item)
                            this.seriesList[item] = s
                            if (this.id !== Number.parseInt(item)) {
                                s.setAxisRight()
                            }
                        }
                        s.updateFromSeriesData(seriesData)

                        if (this.leftMeterIds.indexOf(seriesData.id) !== -1) {
                            this.leftMin = Math.min(this.leftMin, seriesData.min)
                            this.leftMax = Math.max(this.leftMax, seriesData.max)
                        }
                        else {
                            this.rightMin = Math.min(this.rightMin, seriesData.min)
                            this.rightMax = Math.max(this.rightMax, seriesData.max)
                        }

                        // Fix straight line situations.
                        if (this.leftMax === this.leftMin) {
                            this.leftMax += 0.25
                            this.leftMin -= 0.25
                        }
                        if (this.rightMax === this.rightMin) {
                            this.rightMax += 0.25
                            this.rightMin -= 0.25
                        }

                        this.numData = response.data.numData
                        this.lastUpdateTime = response.data.lastUpdateTime
                        this.seriesList[item] = s
                        numberIdsComplete--
                        if (numberIdsComplete === 0) {
                            this.updateChart()
                        }
                    }
                ).catch(
                    error => {
                        console.log(error)
                    }
                ).then(() => {
                    this.loading = false
                })
            })
        }, 100)

        this.updateGraphDebounce = _.debounce(() => {
            if (!this.chartReady) {
                this.setupChart()
            }
            this.lastDataIndex = 0

            this.chart.dataSource.url = `${this.company.buildCompanyApiInternalRoute('data-meter-legacy/' + this.id)}?startDate=${this.startDate.toISOString()}&endDate=${this.endDate.toISOString()}`
            this.chart.dataSource.url = `${this.chart.dataSource.url}&requestNumber=${this.requestNumber}`
            this.chart.dataSource.url = `${this.chart.dataSource.url}&useUTC=${this.useUTC}`
            this.chart.dataSource.url = `${this.chart.dataSource.url}&ids=${this.rightMeterIds}`
            this.lastLoadedDate = null

            if (!_.isNil(this.chart.series) && this.chart.series.length > 0) {
                this.am4SeriesResetBullets(this.chart)
            }
            else {
            }

            // Do Reset things
            if (this.numData > 0) {
                let valueAxis = this.chart.yAxes.getIndex(0)
                this.am4SetupValueAxis(valueAxis, this.leftMin, this.leftMax)

                // Reset the right Value Axis if it is presetn
                if (this.chart.yAxes.length > 1) {
                    let valueAxisRight = this.chart.yAxes.getIndex(1)
                    this.am4SetupValueAxis(valueAxisRight, this.rightMin, this.rightMax)
                }

                let dateAxis = this.chart.xAxes.getIndex(0)
                this.am4ResetDateAxis(this.startDate, this.endDate, dateAxis)

                let valueAxisRight = this.chart.yAxes.getIndex(1)
                valueAxisRight.visible = this.pairPointSelected
                if (this.pairPointSelected) {
                    let seriesB = this.seriesList[this.pairedPoint]
                    seriesB.setupSeries()
                    seriesB.setupThresholds()
                    seriesB.show()
                    let am4SeriesB = seriesB.getSeries()
                    am4SeriesB.name = this.pairPoints[this.pairedPoint].Name
                }

                this.formatter.timezone = this.timezone

                this.loadingChartData = true
                this.chart.data = []
                this.chart.dataSource.reloadFrequency = am4Settings.RELOAD_FREQUENCY
                this.chart.dataSource.load()

                _.each(this.seriesList, (series, id) => {
                    if (_.indexOf(this.leftMeterIds, id) !== -1) {
                        series.show()
                        series.setAxisLeft()
                    }
                    else if (_.indexOf(this.rightMeterIds, id) !== -1) {
                        series.show()
                        series.setAxisRight()
                    }
                    else {
                        series.hide()
                    }
                })

                if (this.legend) {
                    this.legend.dispose()
                }
                this.legend = this.setupLegend()
                this.am4SetupScrollbar(this.chart, this.isHeavyLoad, this.formatter, this.timezone)
            }
        }, 100)
    },
    mounted() {
        this.chart = this.am4CreateChart(this.$refs.meterGraph, am4charts.XYChart)
        if (this.filterDataReady) {
            // this.setupChart()
            this.loadData()
        }
    },
    methods: {
        loadData() {
            this.loadFunctionTmp()
        },
        updateChart() {
            if (!this.filterDataReady) {
                return false
            }
            this.updateGraphDebounce()

        },
        setupChart() {
            this.chartReady = false // Chart Info
            this.lastDataIndex = 0
            if (this.numData === 0) {
                return
            }
            this.loadingChartData = true

            // Axis
            let dateAxis = this.am4CreateDateAxis(this.chart)
            let valueAxis = this.am4CreateValueAxis(this.chart)
            let valueAxisB = this.am4CreateValueAxis(this.chart)
            this.legend = null

            let series = null
            if (!_.has(this.seriesList, this.id)) {
                series = new SeriesLegacy(this.chart, this.id)
            }
            else {
                series = this.seriesList[this.id]
            }
            series.setupSeries()

            let am4Series = series.getSeries()

            // Chart Configuration & plugins
            this.setupChartConfiguration()
            this.am4SetupChartPlugins(this.chart)

            // Date Axis
            this.setupDateAxis(dateAxis)
            this.am4SetupDateAxisTooltip(dateAxis, am4Series, this.timezone)
            this.setupDateAxisGrouping(dateAxis)

            // Value Axis
            this.am4SetupValueAxis(valueAxis, this.leftMin, this.leftMax)
            this.am4SetupValueAxis(valueAxisB, this.rightMin, this.rightMax)
            valueAxisB.visible = this.hasPairedPoint

            // Alarm Guides
            let alarmValues = series.setupThresholds()

            // The Series
            am4Series.name = this.name
            this.seriesList[this.id] = series


            this.am4SetupSeriesGrouping(am4Series, alarmValues, this.id)
            let scrollBar = this.am4SetupScrollbar(this.chart, this.isHeavyLoad, this.formatter, this.timezone)

            this.chartEvents.push(this.chart.dataSource.events.on('parseended', ev => {
                if (parseInt(ev.target.data.requestNumber) !== this.requestNumber) {
                    ev.target.data = []
                }
                let scrollAxis = this.isHeavyLoad
                    ? null
                    : this.chart.scrollbarX.scrollbarChart.xAxes.getIndex(0)
                ev.target.data = ev.target.data.data
                let start = null
                let filling = false
                let lineColor = am4core.color('#3d81f7')
                let backfillColor = am4core.color('#cccf1c')
                for (let index = 0; index < ev.target.data.length; index++) {
                    let item = ev.target.data[index]
                    // Check if we should stop filling the data
                    // TODO figure out how to filter out Null values now.
                    if (item.date === 0) {
                        this.loadingChartData = false
                        this.chart.dataSource.reloadFrequency = am4Settings.RELOAD_FREQUENCY_FINISHED
                        ev.target.data.splice(index, 1)
                        index--
                        continue
                    }

                    // Default set date & color for the lines.
                    item.date = new Date(item.date)
                    item.color = lineColor
                    item.backgroundColor = lineColor

                    // If the Point is backfilled and the next point is backfilled. make sure that it is marked that we are filling &
                    // tag a start date for adding the range later.
                    // This represents if we should check the next point for backfill data & if we should if the next point is backfilled
                    // (On Last point) Or (not on the last point && next point isBackfilled.)
                    if (!this.isHeavyLoad && scrollAxis) {
                        let onLastPoint = index >= this.chart.data.length - 1
                        let nextBackfilled = onLastPoint || (!onLastPoint && this.chart.data[index + 1].isBackfilled)
                        if (item.isBackfilled && nextBackfilled) {
                            item.backgroundColor = backfillColor
                            filling = true
                            if (start === null) {
                                start = item.date
                            }
                        }
                        // If this point or the next point is not backfilled then we should finalize the Axis range with the backfill color
                        else if (start !== null && !this.isHeavyLoad && scrollAxis) {
                            // Add the backfilled range we built to the range list
                            let range = scrollAxis.axisRanges.create()
                            range.axisFill.fillOpacity = 1
                            range.strokeOpacity = 1
                            range.date = start
                            range.endDate = item.date
                            range.axisFill.fill = backfillColor
                            start = null
                            filling = false
                        }
                    }

                    // In either case add the point to the graph series.
                    ev.target.data[index] = item
                }
                if (filling && !this.isHeavyLoad && scrollAxis) {
                    let range = scrollAxis.axisRanges.create()
                    range.axisFill.fillOpacity = 1
                    range.strokeOpacity = 1
                    range.date = start
                    range.endDate = ev.target.data[ev.target.data.length - 1].date
                    range.axisFill.fill = backfillColor
                }
                this.lastDataIndex = this.chart.data.length - 1 + ev.target.data.length
                this.lastLoadedDate = ev.target.data[ev.target.data.length - 1].date
            }))

            // Adjust the gap size any time the interval for the underlying data being rendered changes.
            this.chartEvents.push(this.chart.events.on('datavalidated', target => {
                this.updateGapAndMinBulletDistance(dateAxis, scrollBar)

                // This is to force a re-render of the scrollbar where it resizes things so that the series properly
                // displays & adjusts scale based on the newly added data.
                // Without this, the scrollbar may not properly update for reasons that are unknown to me at the moment.
                // This is an incredibly subtle & extremely
                // fast visual change, knowing exactly what it is & when I still can't perceive it on local.

                this.chart.scrollbarX.maxWidth--
                setTimeout(() => {
                    this.chart.scrollbarX.maxWidth++
                }, 1)
                this.chart.svgContainer.measure()
            }))

            this.chartReady = true
        },
        setupChartConfiguration() {
            this.am4SetupChartConfiguration(this.chart, this.formatter, am4Settings.RELOAD_FREQUENCY)
            this.chart.dataSource.adapter.add("incrementalParams", params => {
                if (!_.isNil(this.lastLoadedDate)) {
                    let date = this.lastLoadedDate
                    date.setSeconds(date.getSeconds() + 1)
                    params.lastLoad = date.toISOString()
                }
                params.requestNumber = this.requestNumber;
                params.incremental = "y";
                return params;
            });
        },
        setupDateAxis(dateAxis) {
            this.am4ResetDateAxis(this.startDate, this.endDate, dateAxis)
            this.am4SetupDateAxis(dateAxis, this.formatter)
        },
        // TODO. Convert to mixin
        setupDateAxisGrouping(dateAxis) {
            dateAxis.groupData = true
            dateAxis.groupCount = 750
            let interval = this.getIntervals(dateAxis.groupCount)
            dateAxis.groupIntervals.setAll(interval)

            this.chartEvents.push(dateAxis.events.on('datarangechanged', target => {
                if (dateAxis.groupData === false) {
                    return
                }

                let currentInterval = dateAxis.groupIntervals.getIndex(0)
                let interval = this.getIntervals(dateAxis.groupCount, dateAxis.end - dateAxis.start)
                if (interval[0].timeUnit !== currentInterval.timeUnit || interval[0].count !== currentInterval.count) {
                    dateAxis.groupIntervals.setAll(interval)
                    this.chart.cursor.disabled = true
                    this.chart.series.each((item, key) => {
                        item.dataGrouped = false
                        dateAxis.groupSeriesData(item)
                    })
                    setTimeout(() => {
                        this.chart.cursor.disabled = false
                        this.chart.series.each(item => {
                            item.dataGrouped = true
                        })
                        this.updateGapAndMinBulletDistance(dateAxis)
                    }, 10)
                }
            }))
        },
        setupScrollbar(dateAxis, valueAxis, series) {
            this.am4SetupScrollbar(this.chart, this.isHeavyLoad, this.formatter, this.timezone)
        },
        getIntervals(groupCount, ratioUsed = 1) {
            return this.am4DateAxisGetIntervals(groupCount, this.startDate, this.endDate, ratioUsed)
        },
        updateGapAndMinBulletDistance(dateAxis) {
            // The gaps are assumed to be minutes.  We need to increase or decrease their time based on the data's baseInterval
            return this.am4UpdateGapAndMinBulletDistance(this.chart, dateAxis, this.useShortGaps, this.isHeavyLoad, this.hasPairedPoint)
        },
        setupLegend() {
            if (!this.isHeavyLoad) {
                return null
            }

            let legend = new am4maps.Legend()
            legend.parent = this.chart.chartContainer
            legend.align = 'center'
            legend.padding(10, 15, 10, 15)
            legend.data = []
            let ids = this.leftMeterIds.concat(this.rightMeterIds)
            _.each(ids, id => {
                let series = this.seriesList[id]
                legend.data.push({
                    name: series.getSeries().name,
                    id: series.id,
                    fill: series.color,
                })
            })

            // Make sure we can show and hide series
            legend.itemContainers.template.events.on("hit", ev => {
                let id = ev.target.dataItem.dataContext.id
                let series = this.seriesList[id]
                if (series.visible) {
                    series.hide()
                }
                else {
                    series.show()
                }
            });

            return legend
        },
    },
    watch: {
        environment(val, old) {
            console.log(`Environment Changed`)
            console.log(`Old Environment: ${old}`)
            console.log(`New Environment: ${val}`)
            console.log(val)
        },
        startDate(val, old) {
            if (old !== val) {
                this.loadData()
            }
        },
        endDate(val, old) {
            if (old !== val) {
                this.loadData()
            }
        },
        useShortGaps() {
            this.loadData()
        },
        useUTC() {
            if (this.useUTC) {
                this.$emit('update:timezone', 'UTC')
            }
            else {
                this.$emit('update:timezone', this.companyTimezone)
            }
            this.loadData()
        },
        showPairedPoint() {
            if (this.showPairedPoint && !_.isNil(this.pairPointId)) {
                this.rightMeterIds = [`${this.pairPointId}`]
                this.pairedPoint = this.pairPointId
            }
            else {
                this.rightMeterIds = []
                this.pairedPoint = null
                if (_.has(this.seriesList, `${this.pairPointId}`)) {
                    this.seriesList[this.pairPointId].removeSeries()
                }
            }
            this.loadData()
        },
        timezone() {
            if (this.timezone !== 'UTC') {
                this.companyTimezone = this.timezone
            }
            this.loadData()
        },
        filterDataReady(val, old) {
            if (this.filterDataReady && !old) {
                this.loadData()
            }
        },
        pairedPoint() {
            this.rightMeterIds = []
            if (!_.isNil(this.pairedPoint)) {
                this.rightMeterIds = [`${this.pairedPoint}`]
            }
            this.loadData()
        },
    },
    computed: {
        graphHeight(){
            let clientHeight = document.documentElement.clientHeight;
            clientHeight = Math.max(clientHeight - 400, 450);
            return "height: " + clientHeight.toString() + "px;"
        },
        prettyLastUpdateTime() {
            if (!_.isNil(this.lastUpdateTime)) {
                let time = new Date(this.lastUpdateTime)
                return `On: ${time.toLocaleString("en-US", {timeZone: this.timezone})}`
            }
            else {
                return 'Never'
            }
        },
        /* This is a graph with a lot of data points
         * 15,000 readings translates to:
         * interval             |  Time on Graph
         * 15 minute check-in   | 5.2 Months
         * 5 minute check-in    | 1.9 months
         * 1 minute check-in    | 10.4 Days
         * 1 Second Check-in    | 4.2 Hours
         */
        isHeavyLoad() {
            return this.numData > 15000 ||
                this.leftMeterIds.length + this.rightMeterIds.length > 1
        },
        hasPairedPoint() {
            return !_.isNil(this.pairPointOptions) && Object.keys(this.pairPointOptions).length > 0
        },
        pairPointSelected() {
            return !_.isNil(this.pairedPoint)
        },
    },
    beforeUnmount() {
        for (let index = 0; index < this.chartEvents.length; index++) {
            this.chartEvents[index].dispose()
        }
        this.chartEvents = []
        this.chart.dataSource.adapter.remove("incrementalParams")

        if (!_.isNil(this.chart)) {
            this.chart.dispose()
        }
        this.chart = null
    }
}

</script>

<style scoped>
#meter-graph {
    height: 40em
}
.dark-theme {
    background-color: #30303d;
    color: #fff;
}
</style>